import {
  AddToCalendarAction,
  AuthenticatedEvent,
  DownloadDocumentAction,
  ExitAction,
  OpenExternalUrlAction,
  SessionExpiredEvent,
} from './messages'
import type {
  AddToCalendarDelegate,
  AuthTokenDelegate,
  CreateExternalUrlHandleOptions,
  DocumentDownloadDelegate,
  ExternalUrlDelegate,
  ExternalUrlOpenHandle,
  LifecycleDelegate,
  PlatformDelegate,
} from './types'

type FlutterCallHandler = (
  handlerName: string,
  ...args: unknown[]
) => Promise<unknown>

interface NativeWindow extends Window {
  ReactNativeWebView?: {
    postMessage(message: string): void
  }
  flutter_inappwebview?: {
    callHandler?: FlutterCallHandler
  }
  webkit?: {
    messageHandlers?: {
      nativeApp?: {
        postMessage(data: unknown): void
      }
    }
  }
  Android?: {
    postMessage(message: string): void
  }
}

type NativeMessage = object

class NativeExternalUrlDelegate implements ExternalUrlDelegate {
  createHandle(
    _options?: CreateExternalUrlHandleOptions,
  ): ExternalUrlOpenHandle {
    return {
      open(url: string) {
        sendNativeMessage(OpenExternalUrlAction(url))
      },
      abort() {
        // no-op
      },
    }
  }
}

class NativeDocumentDownloadDelegate implements DocumentDownloadDelegate {
  download(url: string): void {
    sendNativeMessage(DownloadDocumentAction(url))
  }
}

class NativeAddToCalendarDelegate implements AddToCalendarDelegate {
  addToCalendar(icsFile: File): void {
    const reader = new FileReader()
    reader.onloadend = () =>
      sendNativeMessage(AddToCalendarAction(reader.result as string))
    reader.readAsDataURL(icsFile)
  }
}

class NativeLifecycleDelegate implements LifecycleDelegate {
  readonly supportsDelegatedExit = true

  notifyAuthenticated(): void {
    sendNativeMessage(AuthenticatedEvent())
  }

  notifySessionExpired(): void {
    sendNativeMessage(SessionExpiredEvent())
  }

  requestExit(_returnUrl: string): void {
    sendNativeMessage(ExitAction())
  }
}

class NativeAuthTokenDelegate implements AuthTokenDelegate {
  async getToken(): Promise<string | null> {
    const callHandler = (window as NativeWindow).flutter_inappwebview
      ?.callHandler
    if (typeof callHandler !== 'function') {
      return null
    }

    const token = await callHandler('getAuthToken')
    return typeof token === 'string' && token.length > 0 ? token : null
  }
}

/**
 * Platform delegate that allows interfacing with native hosts.
 */
export class NativePlatformDelegate implements PlatformDelegate {
  private readonly _externalUrlDelegate = new NativeExternalUrlDelegate()
  private readonly _documentDownloadDelegate =
    new NativeDocumentDownloadDelegate()

  private readonly _addToCalendarDelegate = new NativeAddToCalendarDelegate()
  private readonly _lifecycleDelegate = new NativeLifecycleDelegate()
  private readonly _authTokenDelegate = new NativeAuthTokenDelegate()

  getExternalUrlDelegate = () => this._externalUrlDelegate
  getDocumentDownloadDelegate = () => this._documentDownloadDelegate
  getAddToCalendarDelegate = () => this._addToCalendarDelegate
  getLifecycleDelegate = () => this._lifecycleDelegate
  getAuthTokenDelegate = () => this._authTokenDelegate

  static get isSupported() {
    return isEmbeddedInNative()
  }
}

function isEmbeddedInNative() {
  const nativeWindow = window as NativeWindow
  return [
    nativeWindow.ReactNativeWebView,
    nativeWindow.flutter_inappwebview,
    nativeWindow.webkit?.messageHandlers?.nativeApp,
    nativeWindow.Android,
  ].some(Boolean)
}

function sendNativeMessage(data: NativeMessage) {
  const nativeWindow = window as NativeWindow
  const message = JSON.stringify(data)

  if (nativeWindow.ReactNativeWebView) {
    nativeWindow.ReactNativeWebView.postMessage(message)
    return
  }

  const callHandler = nativeWindow.flutter_inappwebview?.callHandler
  if (typeof callHandler === 'function') {
    void callHandler('taxfyleMessageHandler', message)
    return
  }

  if (nativeWindow.webkit?.messageHandlers?.nativeApp) {
    nativeWindow.webkit.messageHandlers.nativeApp.postMessage(data)
    return
  }

  if (nativeWindow.Android?.postMessage) {
    nativeWindow.Android.postMessage(message)
    return
  }

  window.parent.postMessage(data, '*')
}
